/**
 * Cache Version Management
 * Automatically updates on every build using git commit hash
 * Only clears cache when there are actual code changes
 */

const CACHE_VERSION_KEY = 'arterra_cache_version';
const LAST_CLEAR_KEY = 'arterra_last_cache_clear';

// Use build version from environment (includes git hash) or timestamp
const BASE_VERSION = import.meta.env.VITE_BUILD_VERSION || `build-${Date.now()}`;

// Major version for forced cache clears (increment when needed)
const MAJOR_VERSION = '3.0';

// Combine major version with build version
const CURRENT_CACHE_VERSION = `${MAJOR_VERSION}-${BASE_VERSION}`;

export const checkAndMigrateCacheVersion = (forceClean = false): void => {
  try {
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    const lastClear = localStorage.getItem(LAST_CLEAR_KEY);

    // Extract git hash from version if available
    const currentHash = CURRENT_CACHE_VERSION.match(/([a-f0-9]{7})/)?.[1];
    const storedHash = storedVersion?.match(/([a-f0-9]{7})/)?.[1];

    // Check if we need to clear cache
    let shouldClear = false;

    if (forceClean) {
      shouldClear = true;
    } else if (!storedVersion) {
      shouldClear = true;
    } else if (!storedVersion.startsWith(MAJOR_VERSION)) {
      shouldClear = true;
    } else if (currentHash && storedHash && currentHash !== storedHash) {
      // Only clear if git hash changed (actual code changes)
      shouldClear = true;
    } else if (!currentHash && storedVersion !== CURRENT_CACHE_VERSION) {
      // Fallback for non-git environments
      shouldClear = true;
    }

    // Also clear if it's been more than 7 days (weekly cleanup)
    if (!shouldClear && lastClear) {
      const daysSinceLastClear = (Date.now() - parseInt(lastClear)) / (1000 * 60 * 60 * 24);
      if (daysSinceLastClear > 7) {
        shouldClear = true;
      }
    }

    if (shouldClear) {
      // Clear all localStorage items except the new version
      const keysToKeep = [CACHE_VERSION_KEY];
      const allKeys = Object.keys(localStorage);

      allKeys.forEach(key => {
        if (!keysToKeep.includes(key)) {
          localStorage.removeItem(key);
        }
      });

      // Clear sessionStorage
      sessionStorage.clear();

      // Clear IndexedDB if exists
      if (typeof indexedDB !== 'undefined') {
        const databases = [
          'firebaseLocalStorage',
          'firebaseLocalStorageDb',
          'firebase-installations-database'
        ];

        databases.forEach(dbName => {
          try {
            indexedDB.deleteDatabase(dbName);
          } catch {
            // Silently handle errors
          }
        });
      }

      // Clear cookies
      document.cookie.split(';').forEach(cookie => {
        const [name] = cookie.trim().split('=');
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      });

      // Set the new version and last clear time
      localStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);
      localStorage.setItem(LAST_CLEAR_KEY, Date.now().toString());
    }
  } catch {
    // Silently handle cache version check errors
  }
};

// Check for stale auth data
export const cleanStaleAuthData = (): void => {
  try {
    // Remove old auth tokens that might be causing issues

    // Check for Firebase auth keys
    const firebaseAuthKeys = Object.keys(localStorage).filter(key =>
      key.startsWith('firebase:authUser:') ||
      key.startsWith('firebaseui:') ||
      key.includes('firebase')
    );

    // If we have old Firebase auth data, check if it's stale
    firebaseAuthKeys.forEach(key => {
      try {
        const data = localStorage.getItem(key);
        if (data) {
          const parsed = JSON.parse(data);
          // Check if token is expired or very old (> 7 days)
          if (parsed.stsTokenManager) {
            const expirationTime = parsed.stsTokenManager.expirationTime;
            const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

            if (expirationTime < Date.now() || expirationTime < sevenDaysAgo) {
              localStorage.removeItem(key);
            }
          }
        }
      } catch {
        // If we can't parse it, it's probably corrupted - remove it
        localStorage.removeItem(key);
      }
    });
  } catch {
    // Silently handle auth data cleanup errors
  }
};

// Run on app initialization
export const initializeCacheManagement = (): void => {
  checkAndMigrateCacheVersion();
  cleanStaleAuthData();
};
